/**
 * 数字雨的挂载/调度层（主线程）。
 *
 * 两条路径：
 * 1) 支持 OffscreenCanvas + Worker（Android WebView / iOS 16.4+）：
 *    canvas 通过 transferControlToOffscreen() 交给 Worker，主线程完全不参与逐帧绘制，
 *    加载时的长任务不会再让数字流卡住。
 * 2) 不支持时回退到主线程 requestAnimationFrame 渲染（逻辑与以前一致，共用同一个引擎）。
 */
import RainWorker from './matrixRainWorker?worker';
import {
  RAIN_MAX_FRAME_DELTA_MS,
  RainLayer,
  RainState,
  createRainState,
  renderRainFrame,
  resolveFontSize,
  seedRain,
} from './matrixRainEngine';

interface Attachment {
  id: number;
  canvas: HTMLCanvasElement;
  host: HTMLElement;
  className: string;
  opaque: boolean;
  dispose: () => void;
}

let worker: Worker | null = null;
let workerBroken = false;
let workerSupportKnown: boolean | null = null;
let nextAttachmentId = 1;
const attachments = new Map<number, Attachment>();

// ---- 主线程回退路径的共享状态 ----
// 启动层与首页背景层短暂同时存在，两层画的是同一场雨，所以要共享雨滴状态；
// 并且只让最早挂载的那层推进位置，避免交接时下落速度翻倍。
const mainThreadState: RainState = createRainState();
const mainThreadOrder: number[] = [];

let sharedResizeObserver: ResizeObserver | null = null;
let sharedWindowListenerBound = false;
let pendingViewportSync = false;
let renderModeLogged = false;

const resolveRatio = (): number => Math.min(window.devicePixelRatio || 1, 2);

const sendViewport = (): void => {
  if (!worker || workerBroken) return;
  // 两个 canvas 都是全屏铺满，取第一个有效尺寸即可
  for (const entry of attachments.values()) {
    const width = entry.canvas.clientWidth;
    const height = entry.canvas.clientHeight;
    if (width <= 0 || height <= 0) continue;
    worker.postMessage({ type: 'viewport', width, height, ratio: resolveRatio() });
    return;
  }
};

const scheduleViewportSync = (): void => {
  if (pendingViewportSync) return;
  pendingViewportSync = true;
  // 合并同一帧内的多次尺寸变化，避免 postMessage 风暴
  requestAnimationFrame(() => {
    pendingViewportSync = false;
    sendViewport();
  });
};

const handleWorkerFailure = (): void => {
  if (workerBroken) return;
  workerBroken = true;
  // 已经转移给 Worker 的 canvas 拿不回来，只能原地换一个新的用主线程渲染，
  // 否则用户会看到一块永远不动的雨。
  const current = Array.from(attachments.values());
  attachments.clear();
  for (const entry of current) {
    entry.dispose();
    entry.canvas.remove();
    const fallback = attachToMainThread(entry.host, entry.className, entry.opaque);
    attachments.set(fallback.id, fallback);
  }
};

const ensureWorker = (): Worker => {
  if (worker) return worker;
  const instance = new RainWorker();
  instance.onerror = () => {
    handleWorkerFailure();
  };
  worker = instance;
  return instance;
};

export const isRainWorkerSupported = (): boolean => {
  if (workerSupportKnown !== null) return workerSupportKnown;
  try {
    // 逃生开关：URL 带 ?rain=main 时强制走主线程渲染（排查设备兼容问题时用）
    if (new URLSearchParams(window.location.search).get('rain') === 'main') {
      workerSupportKnown = false;
      return workerSupportKnown;
    }
    workerSupportKnown =
      typeof Worker !== 'undefined' &&
      typeof OffscreenCanvas !== 'undefined' &&
      typeof (HTMLCanvasElement.prototype as unknown as { transferControlToOffscreen?: unknown })
        .transferControlToOffscreen === 'function';
  } catch {
    workerSupportKnown = false;
  }
  return workerSupportKnown;
};

const bindSharedObservers = (): void => {
  if (typeof ResizeObserver !== 'undefined') {
    if (!sharedResizeObserver) {
      sharedResizeObserver = new ResizeObserver(() => scheduleViewportSync());
    }
    return;
  }
  if (!sharedWindowListenerBound) {
    sharedWindowListenerBound = true;
    window.addEventListener('resize', scheduleViewportSync);
  }
};

const attachToWorker = (host: HTMLElement, className: string, opaque: boolean): Attachment => {
  const canvas = document.createElement('canvas');
  canvas.className = className;
  canvas.setAttribute('aria-hidden', 'true');
  host.appendChild(canvas);

  const instance = ensureWorker();
  const offscreen = canvas.transferControlToOffscreen();
  const id = nextAttachmentId;
  nextAttachmentId += 1;

  instance.postMessage({ type: 'attach', id, canvas: offscreen, opaque }, [offscreen]);
  bindSharedObservers();
  sharedResizeObserver?.observe(canvas);
  scheduleViewportSync();

  return {
    id,
    canvas,
    host,
    className,
    opaque,
    dispose: () => {
      sharedResizeObserver?.unobserve(canvas);
      worker?.postMessage({ type: 'detach', id });
    },
  };
};

const attachToMainThread = (host: HTMLElement, className: string, opaque: boolean): Attachment => {
  const canvas = document.createElement('canvas');
  canvas.className = className;
  canvas.setAttribute('aria-hidden', 'true');
  host.appendChild(canvas);

  const id = nextAttachmentId;
  nextAttachmentId += 1;
  mainThreadOrder.push(id);

  const ctx = canvas.getContext('2d', { alpha: !opaque, desynchronized: true });
  const layer: RainLayer = {
    opaque,
    width: 0,
    height: 0,
    ratio: 1,
    fontSize: 16,
    backgroundStart: performance.now(),
  };

  let frameId = 0;
  let lastTimestamp = 0;
  let running = true;

  const resize = () => {
    if (!ctx) return;
    const ratio = resolveRatio();
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    // 改 canvas.width 会重置上下文状态，必须重新设变换
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    const fontSize = resolveFontSize(width);
    seedRain(mainThreadState, Math.ceil(width / fontSize), height / fontSize);

    layer.width = width;
    layer.height = height;
    layer.ratio = ratio;
    layer.fontSize = fontSize;

    if (opaque) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.clearRect(0, 0, width, height);
    }
  };

  const loop = (timestamp: number) => {
    if (!running || !ctx) return;
    const delta =
      lastTimestamp === 0 ? 16.67 : Math.min(timestamp - lastTimestamp, RAIN_MAX_FRAME_DELTA_MS);
    lastTimestamp = timestamp;
    renderRainFrame({
      ctx,
      state: mainThreadState,
      layer,
      timestamp,
      delta,
      isDriver: mainThreadOrder[0] === id,
    });
    frameId = requestAnimationFrame(loop);
  };

  resize();
  frameId = requestAnimationFrame(loop);

  let resizeObserver: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
  } else {
    window.addEventListener('resize', resize);
  }

  return {
    id,
    canvas,
    host,
    className,
    opaque,
    dispose: () => {
      running = false;
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resize);
      const index = mainThreadOrder.indexOf(id);
      if (index >= 0) mainThreadOrder.splice(index, 1);
    },
  };
};

/**
 * 往 host 里挂一块全屏数字雨画布，返回卸载函数。
 * 优先走 Worker；任何一步失败都自动回退到主线程渲染。
 */
export const mountRainCanvas = (
  host: HTMLElement,
  className: string,
  transparent: boolean,
): (() => void) => {
  let entry: Attachment | null = null;

  if (isRainWorkerSupported() && !workerBroken) {
    try {
      entry = attachToWorker(host, className, !transparent);
    } catch {
      // transferControlToOffscreen 抛错（旧内核 / 已被转移过的 canvas）：
      // 这个 canvas 可能已经不可用，直接换回主线程渲染
      entry = null;
      workerBroken = true;
    }
  }

  if (!entry) {
    entry = attachToMainThread(host, className, !transparent);
  }

  // 只报一次：方便在真机日志里确认数字雨到底跑在 Worker 还是主线程
  if (!renderModeLogged) {
    renderModeLogged = true;
    console.info(`[matrix-rain] render mode: ${getRainRenderMode()}`);
  }

  const attached = entry;
  attachments.set(attached.id, attached);

  return () => {
    attachments.delete(attached.id);
    attached.dispose();
    attached.canvas.remove();
  };
};

/** 当前生效的渲染通道，便于排查「到底有没有走上 Worker」 */
export const getRainRenderMode = (): 'worker' | 'main' =>
  isRainWorkerSupported() && !workerBroken ? 'worker' : 'main';
